import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { queryClient } from '@/shared';

import type { PropsWithChildren } from 'react';

/**
 * React Query Devtools 를 감싸는 클래스 — **하단 탭바가 뜨는 폭에서는 렌더하지 않는다.**
 *
 * ## 왜 필요한가 (실측)
 * devtools 의 플로팅 토글은 화면 **우하단 고정**인데, 그 자리가 `BottomTabBar` 의 5번째 칸
 * (「더보기」)이다. 「더보기」는 `moreMenuItems` 10개(예측·금융 캘린더·경제 지표·부동산·김프·
 * AI 리포트·뉴스·자산·마이페이지·공지)로 가는 **유일한 문**이라, 이게 막히면 모바일 폭에서
 * 그 10개 화면에 도달할 방법이 사라진다.
 *
 * 🔴 이 결함은 **DOM 존재 검사로는 안 보인다** — `<nav>` 도 `<button>` 도 멀쩡히 있다.
 * dev.xtrading.kr 375×812 에서 `document.elementFromPoint(탭 중심)` 로 잰 실측:
 *
 *   홈(38,784) svg ✅ / 시세(113,784) svg ✅ / 커뮤니티(188,784) svg ✅ / 관심(263,784) svg ✅
 *   🔴 더보기(338,784) → `<ellipse>` (devtools SVG). 390×844 에서도 동일.
 *
 * **5개 중 정확히 1개만 죽는다** — 전부 죽으면 계측 오류, 전부 살면 결함 없음인데 격리가 맞았다.
 * 운영(xtrading.kr)에서는 같은 측정이 **5/5 통과**이고 `[class*="tsqd"]` 도 0건이다.
 * 즉 지금 이 결함은 **dev 에서만 발현**하고, 하필 davidyoon 이 dev 로 QA 하는 것을 막고 있었다.
 *
 * 🔴 **그런데 「왜 운영엔 없는가」는 규명 못 했다 — 여기에 기대지 말 것.**
 * 라이브러리는 `NODE_ENV !== 'development'` 면 `() => null` 을 내보내는데
 * (`@tanstack/react-query-devtools/build/modern/index.js`), **`Dockerfile` 과
 * `Dockerfile.prod` 는 NODE_ENV 를 똑같이 안 세우고 빌드 명령도 같은 `pnpm run build`** 다.
 * 그런데도 런타임 결과가 갈린다. 번들 grep 으로 가르려 했으나 **양성 대조군이 실패**했다 —
 * 운영 entry 번들에 `QueryClient` 조차 0건이라(코드 스플리팅) 그 파일로는 부재를 증명할 수 없다.
 * → **운영이 지금 깨끗한 것은 이유를 아는 상태가 아니다.** 다음 배포에서 devtools 가 운영에
 *   실릴 가능성을 배제할 수 없으므로, 이 게이트는 dev 편의가 아니라 **운영 방어선**이기도 하다.
 *
 * ## 왜 `buttonPosition` 이동이 아닌가 (기각 근거)
 * 모바일 폭에서는 **네 모서리가 전부 점유돼 있다** — 하단은 탭바가 폭 전체(5칸 × 75px = 375),
 * 상단은 헤더 아이콘 5개(언어·테마·알림·설정·마이, y=10~38)가 폭 전체. 안전한 모서리가 없어
 * `buttonPosition` 을 어디로 옮겨도 무언가를 가린다. **판정 기준 ②(모바일에서 5/5)를 못 만족한다.**
 *
 * ## 왜 환경 플래그 opt-in 이 아닌가 (기각 근거)
 * 기본을 끄면 **PC 에서도 아무도 devtools 를 못 쓴다** — 판정 기준 ①(개발자가 여전히 쓸 수
 * 있어야 한다)에 정면으로 어긋난다. 결함은 모바일 폭에만 있는데 전 폭에서 도구를 뺏는 셈이다.
 *
 * ## 그래서 `lg` 다 — 임의의 숫자가 아니다
 * 🔴 **`BottomTabBar` 가 사라지는 바로 그 브레이크포인트**를 쓴다(`BottomTabBar.tsx:235`
 * = `lg:hidden`). 두 값을 묶어 두면 **겹칠 수 있는 구간 자체가 없어진다.** 한쪽만 바꾸면
 * 다시 겹치므로 `queryProvider-devtools-gate.test.tsx` 가 **두 값이 같은지**를 고정한다
 * (한쪽만 고치는 것을 CI 가 잡는다).
 *
 * 개발자는 `lg`(1024px) 이상에서 devtools 를 그대로 쓴다 — 평소 개발 폭이 전부 여기다.
 * 모바일 폭에서 쿼리를 봐야 하면 창을 넓히면 된다(되돌릴 수 있는 비용). 반대로 지금은
 * **davidyoon 이 모바일 QA 를 아예 못 한다**(되돌릴 수 없는 차단).
 *
 * ## CSS 로 가리는 이유 (JS 미디어쿼리가 아닌 이유)
 * 이 앱은 빌드에 `node prerender.mjs` 가 있다 — JS 로 폭을 재면 **프리렌더 시점에 평가**되어
 * 산출물에 한쪽 상태가 박힌다. CSS 는 그 문제가 없고 리스너도 필요 없다.
 * `display:none` 은 서브트리를 통째로 렌더에서 빼므로 **안쪽의 `position: fixed` 토글까지
 * 같이 사라진다**(fixed 라도 조상이 `display:none` 이면 렌더되지 않는다).
 *
 * ## 이 가드가 못 잡는 것
 * - devtools 가 **포털로 `document.body` 에 붙도록 라이브러리가 바뀌면** 이 래퍼는 무력해진다.
 *   현재(v5.66)는 포털이 아니다 — `ReactQueryDevtools.js:60` 이 인라인 `<div
 *   className="tsqd-parent-container" ref>` 를 렌더하고 `:54` 에서 그 ref 에 mount 한다.
 *   버전을 올릴 때 이 전제를 다시 확인할 것.
 * - **실제로 눌리는지**는 jsdom 에서 검증 불가다(레이아웃·페인트가 없어 `elementFromPoint`
 *   가 의미 있는 값을 안 낸다). 그건 `scripts/verify-devtools-tabbar-overlap.mjs` 가
 *   실제 브라우저로 잰다 — vitest 스위트에는 안 딸려온다.
 */
export const DEVTOOLS_GATE_CLASSNAME = 'hidden lg:block';

export const QueryProvider = ({ children }: PropsWithChildren) => (
  <QueryClientProvider client={queryClient}>
    {children}
    <div className={DEVTOOLS_GATE_CLASSNAME} data-testid="devtools-gate">
      <ReactQueryDevtools />
    </div>
  </QueryClientProvider>
);
